import { OrderProvider, OrderFormat, DraftWaypointStatus } from '@caresend/types';
import { isNullish, objectMap } from '@caresend/utils';
import { initFlowHelpers, addAssociatedIDs } from '../../../../flow/helpers.js';
import { toastError } from '../../../../functions/toast/toast.js';
import { getRoute, getRouter } from '../../../../router/helpers/initRouter.js';
import { orderFlowVisitOverviewRoute } from '../../../../router/routes/orderFlow/locations.js';
import { OrderFlowRouteName } from '../../../../router/routes/orderFlow/model.js';
import { getOrderFlowMaybeAsChildName } from '../../../../router/routes/orderFlow/privateHelpers.js';
import { isLabOfficeUser, isDashboardUser } from '../../../helpers.js';
import { getStore } from '../../../helpers/initStore.js';
import { orderFlowCompletionActions } from './completionActions.js';
import { OrderFlowParam } from './model.js';
import { orderFlowStepValidators } from './stepValidation.js';
import { orderFlowTitleGetters } from './titlesActions.js';

// FLOW CONFIG
/** ID generated, all data fetched/initialized, any required navigation completed */
const getFlowLoading = () => {
    const store = getStore();
    const route = getRoute();
    const { draftOrderID } = route.value.params;
    if (!draftOrderID)
        return true;
    return !store.state.orderFlow.localState.flowLoadingCompleted[draftOrderID];
};
/** All data fetched/initialized. */
const getFlowDataIsReady = (draftOrderID) => {
    const store = getStore();
    const route = getRoute();
    // In some cases the draftOrderID is not in the route yet, but we need
    // to check for data. In these cases, we can pass in the draftOrderID
    // as a param.
    const id = draftOrderID !== null && draftOrderID !== void 0 ? draftOrderID : route.value.params.draftOrderID;
    if (!id)
        return false;
    return !!store.state.orderFlow.localState.flowDataIsReady[id];
};
const setNextLoading = (isLoading) => {
    const store = getStore();
    store.commit('orderFlow/SET_NEXT_BUTTON_LOADING', isLoading);
};
const flowConfig = {
    completionActions: orderFlowCompletionActions,
    getFlowDataIsReady,
    getFlowLoading,
    rootParam: OrderFlowParam.DRAFT_ORDER_ID,
    setNextLoading,
    stepValidators: orderFlowStepValidators,
    titleActions: orderFlowTitleGetters,
};
// STEP BUILDING HELPERS
const addSendForSchedulingSecondaryHeaderComponent = (step) => {
    var _a, _b, _c;
    const store = getStore();
    // store.state as any to be removed when waypoint module migrated to ui-components
    const draftWaypointIDs = Object.keys((_c = (_b = (_a = store.state.orderFlow) === null || _a === void 0 ? void 0 : _a.draftOrder) === null || _b === void 0 ? void 0 : _b.draftWaypoints) !== null && _c !== void 0 ? _c : {});
    const areAllOfAssociatedDwScheduled = draftWaypointIDs.length > 0 && draftWaypointIDs.every((draftWaypointID) => {
        var _a, _b, _c, _d;
        return ((_d = (_c = (_b = (_a = store.state) === null || _a === void 0 ? void 0 : _a.waypoint) === null || _b === void 0 ? void 0 : _b.draftWaypoints) === null || _c === void 0 ? void 0 : _c[draftWaypointID !== null && draftWaypointID !== void 0 ? draftWaypointID : '']) === null || _d === void 0 ? void 0 : _d.status)
            === DraftWaypointStatus.FINALIZED;
    });
    return {
        ...step,
        secondaryHeaderComponent: {
            component: () => import('./secondaryHeaderComponents/SendForSchedulingButton.vue.js'),
            props: {
                label: 'Review order',
                disabled: areAllOfAssociatedDwScheduled,
            },
        },
    };
};
const buildOrderStep = (routeName, associatedID, associatedParam = OrderFlowParam.DRAFT_ORDER_ID, subSteps, parentID, parentRouteName, parentAssociatedParam, additionalParams) => addAssociatedIDs({
    associatedParam,
    routeName,
    subSteps,
}, associatedID, parentID, parentRouteName, parentAssociatedParam, additionalParams);
// DRAFT ORDER (ROOT) STEPS
// TODO: Document `asChild`
const getOrderOrderFlowManualInputStep = (bookingID, draftOrderID, asChild = true) => addSendForSchedulingSecondaryHeaderComponent(buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_MANUAL_INPUT, asChild), bookingID, OrderFlowParam.BOOKING_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined));
// TODO: optional procedure ID?
const getOrderOrderFlowMedicalDetailsStep = (bookingID, draftOrderID, procedureID, asChild = true) => buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ORDER_MEDICAL_DETAILS, asChild), bookingID, OrderFlowParam.BOOKING_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined, procedureID ? {
    [OrderFlowParam.PROCEDURE_ID]: procedureID,
} : undefined);
const getOrderOrderFlowAdditionalInformationStep = (bookingID, draftOrderID, procedureID, asChild = true) => buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION, asChild), bookingID, OrderFlowParam.BOOKING_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined, procedureID ? {
    [OrderFlowParam.PROCEDURE_ID]: procedureID,
} : undefined);
const getOrderFlowOrderProviderStep = (bookingID, draftOrderID, asChild = true) => buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ORDER_PROVIDER, asChild), bookingID, OrderFlowParam.BOOKING_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined);
const getOrderPDFUploadStep = (bookingID, draftOrderID, procedureID, asChild = true) => addSendForSchedulingSecondaryHeaderComponent(buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_PDF_UPLOAD, asChild), bookingID, OrderFlowParam.BOOKING_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined, procedureID ? {
    [OrderFlowParam.PROCEDURE_ID]: procedureID,
} : undefined));
const getOrderOrderFlowOrderFulfillerStep = (draftOrderID) => buildOrderStep(OrderFlowRouteName.ORDER_FLOW_ORDER_FULFILLER, draftOrderID, OrderFlowParam.DRAFT_ORDER_ID);
const getScheduleStep = (draftOrderID, asChild = true) => buildOrderStep(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_SCHEDULE, asChild), draftOrderID, OrderFlowParam.DRAFT_ORDER_ID, undefined, asChild ? draftOrderID : undefined, asChild ? OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW : undefined, asChild ? OrderFlowParam.DRAFT_ORDER_ID : undefined);
const getOrderBulkOrderInputStep = (draftOrderID) => buildOrderStep(OrderFlowRouteName.ORDER_FLOW_BULK_UPLOAD, draftOrderID, OrderFlowParam.DRAFT_ORDER_ID);
const getBookingStepMap = (bookingID, draftOrderID, asChild = true) => {
    var _a, _b, _c, _d, _e, _f;
    const map = new Map();
    const store = getStore();
    const booking = (_a = store.state.orderFlow.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
    const bookingProcedures = Object.values((_b = booking === null || booking === void 0 ? void 0 : booking.procedures) !== null && _b !== void 0 ? _b : {});
    if (
    /** TODO: Replace with OrderFormat enum */
    store.state.orderFlow.isCsvImport
        && isDashboardUser()
        && !asChild) {
        map.set(OrderFlowRouteName.ORDER_FLOW_BULK_UPLOAD, getOrderBulkOrderInputStep(draftOrderID));
        return map;
    }
    if (!isNullish(booking === null || booking === void 0 ? void 0 : booking.provider)
        && (booking === null || booking === void 0 ? void 0 : booking.provider) !== OrderProvider.PRESCRIBER) {
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ORDER_PROVIDER, asChild), getOrderFlowOrderProviderStep(bookingID, draftOrderID, asChild));
        return map;
    }
    const flowSettings = store.getters['orderFlow/getFlowSettings'];
    const bookingOrderFormat = (_d = (_c = flowSettings.orderFormatByBookingID) === null || _c === void 0 ? void 0 : _c[bookingID]) !== null && _d !== void 0 ? _d : flowSettings.orderingFormat;
    if (bookingOrderFormat === OrderFormat.MANUAL) {
        let procedureID;
        /**
         * If there is only one procedure in the given booking add the procedureID
         * to additional params for the ORDER_FLOW_ORDER_MEDICAL_DETAILS step.
         * This will only be needed if there is one procedure for navigating back to
         * this step from the schedule step on the first run.
         */
        if (bookingProcedures.length === 1) {
            procedureID = (_e = bookingProcedures[0]) === null || _e === void 0 ? void 0 : _e.id;
        }
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_MANUAL_INPUT, asChild), getOrderOrderFlowManualInputStep(bookingID, draftOrderID, asChild));
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ORDER_MEDICAL_DETAILS, asChild), getOrderOrderFlowMedicalDetailsStep(bookingID, draftOrderID, procedureID, asChild));
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION, asChild), getOrderOrderFlowAdditionalInformationStep(bookingID, draftOrderID, procedureID, asChild));
    }
    else {
        const procedureID = (_f = bookingProcedures[0]) === null || _f === void 0 ? void 0 : _f.id;
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_PDF_UPLOAD, asChild), getOrderPDFUploadStep(bookingID, draftOrderID, procedureID, asChild));
        map.set(getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_ADDITIONAL_INFORMATION, asChild), getOrderOrderFlowAdditionalInformationStep(bookingID, draftOrderID, procedureID, asChild));
    }
    return map;
};
const getVisitOverviewStepSubsteps = (draftOrderID) => {
    const store = getStore();
    const bookings = store.state.orderFlow.draftBookings;
    const substepMaps = objectMap(bookings !== null && bookings !== void 0 ? bookings : {}, (_, bookingID) => getBookingStepMap(bookingID, draftOrderID));
    substepMaps[draftOrderID] = new Map([
        [
            getOrderFlowMaybeAsChildName(OrderFlowRouteName.ORDER_FLOW_SCHEDULE, true),
            getScheduleStep(draftOrderID, true),
        ],
    ]);
    return substepMaps;
};
const getVisitOverviewStep = (draftOrderID) => buildOrderStep(OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW, draftOrderID, OrderFlowParam.DRAFT_ORDER_ID, getVisitOverviewStepSubsteps(draftOrderID), undefined);
// ROOT STEP MAP
const getOrderStepMap = (draftOrderID) => {
    var _a, _b, _c;
    // The step building logic may throw errors in case of missing data. Return
    // empty steps when data is not ready.
    if (!((_a = flowConfig.getFlowDataIsReady) === null || _a === void 0 ? void 0 : _a.call(flowConfig, draftOrderID))) {
        return new Map();
    }
    if (!draftOrderID)
        throw toastError('Missing draft order ID when generating steps.');
    const store = getStore();
    const booking = Object.values((_b = store.state.orderFlow.draftBookings) !== null && _b !== void 0 ? _b : {})[0];
    if (!booking)
        throw Error('Missing booking when generating steps.');
    const stepMap = getBookingStepMap(booking.id, draftOrderID, false);
    if (!isLabOfficeUser()) {
        const { disableInOfficeSupport } = store.state.variables.featureFlags;
        const allowInOfficeDraws = !disableInOfficeSupport;
        if (allowInOfficeDraws) {
            stepMap.set(OrderFlowRouteName.ORDER_FLOW_ORDER_FULFILLER, getOrderOrderFlowOrderFulfillerStep(draftOrderID));
        }
    }
    if (!((_c = store.state.orderFlow.draftOrder) === null || _c === void 0 ? void 0 : _c.isFulfilledByOffice)) {
        stepMap.set(OrderFlowRouteName.ORDER_FLOW_SCHEDULE, getScheduleStep(draftOrderID, false));
    }
    stepMap.set(OrderFlowRouteName.ORDER_FLOW_VISIT_OVERVIEW, getVisitOverviewStep(draftOrderID));
    return stepMap;
};
// HELPERS
const getOrderFlowHelpers = (route) => {
    const stepMap = getOrderStepMap(route.params.draftOrderID);
    return initFlowHelpers(flowConfig, stepMap);
};
// ROUTER HELPERS
/** Dynamically get the first step in the order flow */
const getOrderFlowFirstStepRoute = (draftOrderID) => {
    var _a;
    if (!((_a = flowConfig.getFlowDataIsReady) === null || _a === void 0 ? void 0 : _a.call(flowConfig, draftOrderID)))
        return null;
    const stepMap = getOrderStepMap(draftOrderID);
    const { getFlattenedSteps, getRouteFromStep, } = initFlowHelpers(flowConfig, stepMap);
    const flatSteps = getFlattenedSteps();
    const firstStep = flatSteps[0];
    if (!firstStep)
        throw toastError('Missing first step in order flow.');
    const firstStepRoute = getRouteFromStep(firstStep, draftOrderID);
    return firstStepRoute;
};
/**
 * When entering the flow to create a new order, the user should go to the first
 * step. Add `new` in the query string to support hot reloading (potentially
 * a future local persistence feature as well)
 */
const getOrderFlowNewOrderEntryRoute = (draftOrderID, reOrderID) => {
    const route = reOrderID
        ? orderFlowVisitOverviewRoute(draftOrderID)
        : getOrderFlowFirstStepRoute(draftOrderID);
    if (!route)
        return null;
    return {
        ...route,
        query: { new: 'true', reOrderID },
    };
};
/**
 * When entering the flow to edit an existing order, the user should start at
 * the visit overview step.
 */
const getOrderFlowExistingOrderEntryRoute = (draftOrderID) => orderFlowVisitOverviewRoute(draftOrderID);
/**
 * This helper is used to get the first substep of the visit overview step.
 * If a taskTypeID is provided, it will append the procedureID of the first
 * procedure in the provided booking with that taskTypeID to the route params.
 */
const getOrderFlowVisitOverviewFirstSubstepRoute = (draftOrderID, bookingID, taskTypeID) => {
    var _a;
    const router = getRouter();
    const store = getStore();
    const bookingSteps = getBookingStepMap(bookingID, draftOrderID);
    const step = bookingSteps === null || bookingSteps === void 0 ? void 0 : bookingSteps.values().next().value;
    if (!step)
        throw new Error('There was an issue getting step/routes from the current map');
    const route = getOrderFlowHelpers(router.currentRoute).getRouteFromStep(step, draftOrderID);
    const procedures = store.getters['orderFlow/getDraftBookingProcedures'](bookingID);
    const firstProcedure = (_a = procedures.find((procedure) => procedure.taskTypeID === taskTypeID)) !== null && _a !== void 0 ? _a : procedures[0];
    if (firstProcedure) {
        route.params = {
            ...route.params,
            procedureID: firstProcedure.id,
        };
    }
    return route;
};

export { getBookingStepMap, getOrderFlowExistingOrderEntryRoute, getOrderFlowFirstStepRoute, getOrderFlowHelpers, getOrderFlowNewOrderEntryRoute, getOrderFlowVisitOverviewFirstSubstepRoute, getOrderPDFUploadStep, getVisitOverviewStep };
